import api from './api';
import { productsService } from './productsService';
import { costHistoryService } from './costHistoryService';
import { calculateAverageCost, calculateCostChangePercentage } from '../utils/averageCostCalculator';

// Servicio para manejar entradas de productos
export const entriesService = {
  // Obtener todas las entradas con paginación
  getAll: async (page = 1, limit = 100000, search = '', startDate = '', endDate = '', supplierId = '') => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });
      
      if (search) params.append('search', search);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (supplierId) params.append('supplierId', supplierId);
      
      const response = await api.get(`/entries?${params.toString()}`);
      return response.data; // Retorna { success, data, pagination }
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Error obteniendo entradas');
    }
  },

  // Obtener entrada por ID
  getById: async (id) => {
    try {
      const response = await api.get(`/entries/${id}`);
      return response.data.data; // Extraer el objeto del campo data
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Error obteniendo entrada');
    }
  },

  // Obtener entradas por producto ID
  getByProductId: async (productId) => {
    try {
      console.log('🔄 Solicitando entradas para producto ID:', productId);
      const response = await api.get(`/entries/product/${productId}`);
      console.log('📊 Respuesta del servidor (entries):', response.data);
      console.log('📊 JSON de respuesta (entries):', JSON.stringify(response.data, null, 2));
      
      // Si la respuesta tiene estructura { success, data }
      if (response.data && response.data.success && response.data.data) {
        // Asegurarse de que todas las cantidades sean números
        const processedData = response.data.data.map(entry => ({
          ...entry,
          quantity: parseFloat(entry.quantity) || 0
        }));
        console.log('📊 Datos procesados:', processedData);
        return processedData;
      }
      
      // Si la respuesta es directamente un array
      if (Array.isArray(response.data)) {
        // Asegurarse de que todas las cantidades sean números
        const processedData = response.data.map(entry => ({
          ...entry,
          quantity: parseFloat(entry.quantity) || 0
        }));
        console.log('📊 Datos procesados (array):', processedData);
        return processedData;
      }
      
      // Si no reconocemos el formato, devolvemos un array vacío
      console.warn('⚠️ Formato de respuesta no reconocido en getByProductId:', response.data);
      return [];
    } catch (error) {
      console.error('❌ Error obteniendo entradas del producto:', error);
      if (error.response) {
        console.error('❌ Detalles del error:', error.response.data);
      }
      throw new Error(error.response?.data?.error || error.message || 'Error obteniendo entradas del producto');
    }
  },

  // Obtener entradas por proveedor ID
  getBySupplier: async (supplierId) => {
    try {
      const response = await api.get(`/entries/supplier/${supplierId}`);
      return response.data.data; // Extraer el array del campo data
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Error obteniendo entradas del proveedor');
    }
  },

  // Alias para getBySupplierId
  getBySupplierId: async (supplierId) => {
    try {
      const response = await api.get(`/entries/supplier/${supplierId}`);
      return response.data.data; // Extraer el array del campo data
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Error obteniendo entradas del proveedor');
    }
  },

  // Crear nueva entrada
  create: async (entryData) => {
    try {
      console.log('🔄 Enviando datos de entrada al servidor:', JSON.stringify(entryData));
      const response = await api.post('/entries', entryData);
      console.log('✅ Respuesta del servidor al crear entrada:', response.data);
      
      // El backend ya actualiza stock y costo promedio, evitamos trabajo duplicado en cliente
      
      return response.data.data; // Extraer el objeto del campo data
    } catch (error) {
      console.error('❌ Error al crear entrada:', error);
      if (error.response) {
        console.error('📄 Respuesta del servidor:', error.response.data);
      }
      throw error; // Propagar el error completo para mejor manejo
    }
  },

  // Crear entrada por lotes
  createBatch: async (batchData) => {
    try {
      const response = await api.post('/entries/batch', batchData);
      
      // El backend ya actualiza stock y costo promedio, evitamos trabajo duplicado en cliente
      
      return response.data.data; // Extraer el objeto del campo data
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Error creando lote de entradas');
    }
  },

  // Actualizar entrada
  update: async (id, entryData) => {
    try {
      const response = await api.put(`/entries/${id}`, entryData);
      return response.data.data; // Extraer el objeto del campo data
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Error actualizando entrada');
    }
  },

  // Eliminar entrada
  delete: async (id) => {
    try {
      const response = await api.delete(`/entries/${id}`);
      return response.data; // El delete puede retornar solo success
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Error eliminando entrada');
    }
  },

  // Obtener resumen de entradas
  getSummary: async (startDate, endDate) => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const response = await api.get('/entries/reports/summary', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Error obteniendo resumen de entradas');
    }
  },

  // Actualizar documento de requisición de entrada
  updateDocument: async (id, requisition_document) => {
    try {
      const response = await api.put(`/entries/${id}/document`, { requisition_document });
      return response.data.data; // Extraer el objeto del campo data
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Error actualizando documento de entrada');
    }
  },

  // Actualizar documento de factura de entrada
  updateInvoice: async (id, invoice_document) => {
    try {
      const response = await api.put(`/entries/${id}/invoice`, { invoice_document });
      return response.data.data; // Extraer el objeto del campo data
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Error actualizando documento de factura');
    }
  }
};

/**
 * Actualiza el costo promedio de un producto basado en una nueva entrada
 * 
 * Esta función:
 * 1. Obtiene los datos actuales del producto
 * 2. Calcula el nuevo costo promedio ponderado
 * 3. Actualiza el producto con el nuevo costo
 * 4. Registra el cambio en el historial de costos
 * 
 * @param {number} productId - ID del producto
 * @param {number} newQuantity - Cantidad de la nueva entrada
 * @param {number} newUnitCost - Costo unitario de la nueva entrada
 * @param {string} entryId - ID de la entrada asociada
 * @returns {Promise<number>} - El nuevo costo promedio
 */
const updateProductAverageCost = async (productId, newQuantity, newUnitCost, entryId) => {
  try {
    // 1. Obtener datos actuales del producto
    const product = await productsService.getById(productId);
    if (!product) {
      console.error(`❌ No se encontró el producto con ID ${productId}`);
      return 0;
    }
    
    // Usar el costo promedio si existe, de lo contrario usar el precio
    const currentCost = product.average_cost || product.price || 0;
    const currentStock = product.current_stock || 0;
    
    // 2. Calcular el nuevo costo promedio ponderado
    const newAverageCost = calculateAverageCost(
      currentStock, 
      currentCost, 
      newQuantity, 
      newUnitCost
    );
    
    // Calcular el porcentaje de cambio
    const changePercentage = calculateCostChangePercentage(currentCost, newAverageCost);
    
    console.log(`📊 Costo promedio para ${product.name} (ID: ${productId}):`, {
      stockAnterior: currentStock,
      costoAnterior: currentCost,
      nuevaCantidad: newQuantity,
      nuevoCosto: newUnitCost,
      costoPromedioCalculado: newAverageCost,
      porcentajeCambio: `${changePercentage}%`
    });
    
    // 3. Actualizar el producto con el nuevo costo promedio
    await productsService.update(productId, {
      average_cost: newAverageCost,
      last_average_update: new Date().toISOString()
    });
    
    // 4. Registrar el cambio en el historial de costos
    await costHistoryService.create({
      product_id: productId,
      previous_cost: currentCost,
      new_cost: newAverageCost,
      change_percentage: changePercentage,
      entry_id: entryId,
      date: new Date().toISOString()
    });
    
    return newAverageCost;
  } catch (error) {
    console.error('❌ Error actualizando costo promedio:', error);
    // No lanzamos el error para evitar interrumpir el flujo principal
    return 0;
  }
};

export default entriesService;
